<template>
    <vx-expandable-card>
        <template v-slot:header>Appointment Types - Group Mapping</template>
        <template v-slot:headerRight>
            <vx-input
                v-model="appointmentSearchQuery"
                placeholder="Search appointment types..."
                size="md"
                type="text"
                :disabled="loadingAppointments"
                class="w-64 font-normal"
            >
                <template v-slot:right>
                    <font-awesome-icon widthAuto class="text-xs" :icon="faMagnifyingGlass" />
                </template>
            </vx-input>
        </template>

        <template v-slot:content>
            <div v-if="!hasPermission" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
                Only the team owner can configure integrations.
            </div>
            <div v-else-if="!isConnected" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
                Connect your Acuity account first to map appointment types to groups.
            </div>
            <div v-else class="flex flex-col gap-6 mt-3">
                <div class="mb-4">
                    Assign Acuity appointment types to groups. Contacts will be automatically added to the group when
                    they book an appointment of the assigned type.
                </div>

                <!-- Loading appointments -->
                <div v-if="loadingAppointments" class="flex flex-col items-center justify-center gap-4 py-12">
                    <vx-icon-loading spin class="w-8 h-8 text-sky-600"></vx-icon-loading>
                </div>

                <!-- Connection error -->
                <vx-alert v-else-if="connectionError" color="danger" :closeable="false">
                    <template v-slot:icon>
                        <font-awesome-icon widthAuto :icon="faCircleExclamation" />
                    </template>
                    <div class="flex flex-col gap-2">
                        <div>Unable to load appointment types. Please check your Acuity connection.</div>
                    </div>
                </vx-alert>

                <!-- No appointments -->
                <vx-alert v-else-if="allAppointments.length === 0" color="info" :closeable="false">
                    <template v-slot:icon>
                        <font-awesome-icon widthAuto :icon="faCircleInfo" />
                    </template>
                    No appointment types found in your Acuity account. Create appointment types in Acuity first, then
                    refresh this page.
                </vx-alert>

                <!-- Appointments list -->
                <template v-else>
                    <div class="flex flex-col gap-4">
                        <!-- No search results -->
                        <vx-alert
                            v-if="displayedAppointments.length === 0 && appointmentSearchQuery.trim()"
                            color="info"
                            :closeable="false"
                        >
                            <template v-slot:icon>
                                <font-awesome-icon widthAuto :icon="faCircleInfo" />
                            </template>
                            No appointment types found matching your search.
                        </vx-alert>

                        <!-- Appointments table -->
                        <vx-table
                            v-if="displayedAppointments.length > 0"
                            class="xl:grid-cols-[2fr_3fr] mt-0"
                            rowBreakpoint="xl"
                        >
                            <vx-table-row isHeader>
                                <div>Appointment Type</div>
                                <div>Assigned Groups</div>
                            </vx-table-row>

                            <vx-table-row v-for="appointment in displayedAppointments" :key="appointment.id">
                                <vx-table-row-title>
                                    <div class="flex flex-col gap-1">
                                        <span class="break-all lg:line-clamp-2" :title="appointment.name">{{
                                            appointment.name
                                        }}</span>
                                        <span class="text-xs text-slate-500 flex items-center gap-1">
                                            <font-awesome-icon :icon="faCalendarClock" widthAuto />
                                            {{ appointment.duration }} min
                                        </span>
                                    </div>
                                </vx-table-row-title>
                                <vx-table-row-item contentClasses="w-full">
                                    <template v-slot:label>Assigned Groups:</template>
                                    <vx-selectable
                                        placeholder="Search for a group..."
                                        :options="groupOptions"
                                        label="name"
                                        :reduce="(group: Group) => group.group_id"
                                        :modelValue="appointmentGroupMappings[appointment.id]"
                                        @update:modelValue="updateAppointmentMapping(appointment.id, $event)"
                                        @search="searchGroups"
                                        :filterable="true"
                                        size="md"
                                        :loading="savingAppointments.includes(appointment.id)"
                                        :disabled="savingAppointments.includes(appointment.id)"
                                    >
                                        <template v-slot:option="{ option }">
                                            <div class="flex flex-col gap-1 py-1">
                                                <span class="text-sm font-semibold">{{ option.name }}</span>
                                            </div>
                                        </template>
                                        <template v-slot:selected-option="{ option }">
                                            <span class="text-sm">{{ option.name }}</span>
                                        </template>
                                    </vx-selectable>
                                </vx-table-row-item>
                            </vx-table-row>
                        </vx-table>

                        <!-- Load More button -->
                        <div v-if="hasMoreAppointments" class="flex mt-4">
                            <vx-button @click="loadMoreAppointments" color="muted-dark" size="lg">
                                Load More
                                <font-awesome-icon widthAuto :icon="faSpinner" />
                            </vx-button>
                        </div>
                    </div>
                </template>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script setup lang="ts">
import { ref, computed, reactive, watch } from 'vue';
import {
    VxExpandableCard,
    VxAlert,
    VxTable,
    VxTableRow,
    VxTableRowTitle,
    VxTableRowItem,
    VxButton,
    VxSelectable,
    VxIconLoading,
    VxInput,
} from '@voxie/frontend-components';
import {
    faCircleExclamation,
    faCircleInfo,
    faCalendarClock,
    faSpinner,
    faMagnifyingGlass,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { acuityClient } from '~/services';
import groupsClient from '~/services/groups/groupsClient';
import type { AcuityAppointmentType } from '~/services/acuity/acuityClient.types';
import type { Group } from '~/services/groups/groupsClient.types';
import { catchRequestError } from '~/utils/request';
import { useToasted } from '~/composables/useToasted';
import debounce from '~/utils/debounce';
import { GROUP_STATUSES_CONFIGURABLE, type GroupStatus } from '~/constants/groupStatuses';

const props = defineProps({
    ownsTeam: {
        type: Boolean,
        default: false,
    },
    isConnected: {
        type: Boolean,
        default: false,
    },
});

const toasted = useToasted();
const teamId = Spark.state.currentTeam.id;

const allAppointments = ref<AcuityAppointmentType[]>([]);
const loadingAppointments = ref<boolean>(true);
const connectionError = ref<boolean>(false);
const appointmentsPerPage = 15;
const displayedAppointmentsCount = ref<number>(appointmentsPerPage);
const appointmentSearchQuery = ref<string>('');

const groupCache = ref<Map<string, Group>>(new Map());

const groupOptions = computed(() => {
    const options: Group[] = [];
    const addedIds = new Set<string>();

    // Get currently selected group IDs
    const selectedGroupIds = Object.values(appointmentGroupMappings).filter(Boolean) as string[];

    // Add selected groups first (ensures they're always visible)
    selectedGroupIds.forEach((groupId) => {
        const group = groupCache.value.get(groupId);
        if (group && !addedIds.has(groupId)) {
            options.push(group);
            addedIds.add(groupId);
        }
    });

    // Add all other groups from cache
    groupCache.value.forEach((group) => {
        if (!addedIds.has(group.group_id)) {
            options.push(group);
            addedIds.add(group.group_id);
        }
    });

    return options;
});

// Mappings state (appointment ID -> single group ID)
const appointmentGroupMappings = reactive<Record<number, string | undefined>>({});
const savingAppointments = ref<number[]>([]);

const filteredAppointments = computed(() => {
    let filtered = allAppointments.value;

    if (appointmentSearchQuery.value.trim()) {
        const query = appointmentSearchQuery.value.toLowerCase();
        filtered = filtered.filter((appointment) => appointment.name.toLowerCase().includes(query));
    }

    return filtered;
});

const displayedAppointments = computed(() => {
    return filteredAppointments.value.slice(0, displayedAppointmentsCount.value);
});

const hasMoreAppointments = computed(() => {
    return displayedAppointmentsCount.value < filteredAppointments.value.length;
});

watch(appointmentSearchQuery, () => {
    displayedAppointmentsCount.value = appointmentsPerPage;
});

const hasPermission = computed(() => props.ownsTeam);

const loadAllAppointments = async () => {
    loadingAppointments.value = true;
    connectionError.value = false;
    allAppointments.value = [];

    try {
        const response = await acuityClient.getAppointmentTypes({ teamId });
        allAppointments.value = response.data.appointment_types;
    } catch (error: unknown) {
        connectionError.value = true;
        toasted.error(catchRequestError(error));
    } finally {
        loadingAppointments.value = false;
    }
};

const searchGroups = debounce(async (search: string, loading?: (isLoading: boolean) => void) => {
    loading?.(true);

    try {
        const params: { per_page: number; query?: string; status?: GroupStatus[] } = {
            per_page: 15,
            status: GROUP_STATUSES_CONFIGURABLE,
        };

        if (search) {
            params.query = search;
        }

        const response = await groupsClient.getGroups({ teamId }, params);

        // Add groups to cache (accumulate, don't replace)
        response.data.data.forEach((group) => {
            groupCache.value.set(group.group_id, group);
        });

        // Trigger Vue reactivity
        groupCache.value = new Map(groupCache.value);
    } catch (error) {
        toasted.error(catchRequestError(error));
    } finally {
        loading?.(false);
    }
}, 300);

const loadInitialGroups = async () => {
    try {
        const params: { per_page: number; status?: GroupStatus[] } = {
            per_page: 15,
            status: GROUP_STATUSES_CONFIGURABLE,
        };

        const response = await groupsClient.getGroups({ teamId }, params);

        // Add groups to cache
        response.data.data.forEach((group) => {
            groupCache.value.set(group.group_id, group);
        });

        // Trigger Vue reactivity
        groupCache.value = new Map(groupCache.value);
    } catch (error) {
        toasted.error(catchRequestError(error));
    }
};

// Ensure a group is in the cache (for pre-selected groups)
const ensureGroupInOptions = async (groupId: string) => {
    if (groupCache.value.has(groupId)) {
        return; // Already in cache
    }

    try {
        const response = await groupsClient.getGroup({ teamId }, groupId);
        groupCache.value.set(groupId, response.data);
        // Trigger Vue reactivity
        groupCache.value = new Map(groupCache.value);
    } catch (error) {
        toasted.error(catchRequestError(error));
    }
};

const loadMoreAppointments = () => {
    displayedAppointmentsCount.value += appointmentsPerPage;
};

const initializeAppointmentMappings = async () => {
    const groupIdsToFetch = [];

    for (const appointment of allAppointments.value) {
        if (appointmentGroupMappings[appointment.id] === undefined) {
            // If appointment has a group_id, set it as the initial mapping
            appointmentGroupMappings[appointment.id] = appointment.group_id ?? undefined;

            // Collect pre-selected groups to fetch in parallel
            if (appointment.group_id) {
                groupIdsToFetch.push(appointment.group_id);
            }
        }
    }

    // Fetch all groups in parallel
    await Promise.all(groupIdsToFetch.map((groupId) => ensureGroupInOptions(groupId)));
};

const updateAppointmentMapping = debounce(async (appointmentId: number, selectedGroupId: string | undefined) => {
    if (savingAppointments.value.includes(appointmentId)) {
        return;
    }

    const appointment = allAppointments.value.find((a) => a.id === appointmentId);
    if (!appointment) {
        return;
    }

    const previousGroupId = appointmentGroupMappings[appointmentId];
    const oldGroupId = appointment.group_id;

    savingAppointments.value.push(appointmentId);

    try {
        // Update local state first
        appointment.group_id = selectedGroupId || null;
        appointmentGroupMappings[appointmentId] = selectedGroupId;

        // Collect affected groups (old and new, if different)
        const affectedGroups = new Set<string>();
        if (oldGroupId) {
            affectedGroups.add(oldGroupId);
        }
        if (selectedGroupId) {
            affectedGroups.add(selectedGroupId);
        }

        // For each affected group, rebuild its complete appointment type list from updated local state
        for (const groupId of affectedGroups) {
            const groupAppointmentTypes = allAppointments.value
                .filter((a) => a.group_id === groupId)
                .map((a) => ({
                    appointment_type_id: a.id,
                }));

            await acuityClient.updateGroupMapping({ teamId }, groupId, { appointment_types: groupAppointmentTypes });
        }

        toasted.success('Appointment type mapping updated successfully');
    } catch (error) {
        // Rollback local state on error
        appointment.group_id = oldGroupId || null;
        appointmentGroupMappings[appointmentId] = previousGroupId;
        toasted.error(catchRequestError(error));
    } finally {
        savingAppointments.value = savingAppointments.value.filter((id) => id !== appointmentId);
    }
}, 500);

watch(
    () => props.isConnected,
    async () => {
        if (props.isConnected && hasPermission.value) {
            await loadAllAppointments();
            await loadInitialGroups();
            await initializeAppointmentMappings();
        } else {
            allAppointments.value = [];
            groupCache.value = new Map();
            for (const key in appointmentGroupMappings) {
                delete appointmentGroupMappings[key];
            }
            displayedAppointmentsCount.value = appointmentsPerPage;
            appointmentSearchQuery.value = '';
            loadingAppointments.value = false;
            connectionError.value = false;
        }
    },
    {
        immediate: true,
    }
);
</script>
